import * as z from 'zod/v4'

// Shared contract for POST /learn/parseSyllabus. Lives here (not in the backend) so web can type
// requests against the same source; iOS decodes the same JSON shape (SyllabusParseResult).

// Not a round number: the PDF reaches Gemini base64-encoded at 4/3 its raw size against a 20 MB
// request cap, so 15 MB raw is the ceiling and this leaves room for the prompt and JSON envelope.
export const MAX_SYLLABUS_PDF_BYTES = 14 * 1024 * 1024

export const SyllabusEventTypeSchema = z.enum(['EXAM', 'DEADLINE', 'CLASS_MEETING', 'OTHER'])
export type SyllabusEventType = z.infer<typeof SyllabusEventTypeSchema>

// One dated item. Recurring items are materialized — one event per occurrence, semester-bounded —
// rather than modeled as recurrence rules. Occurrences share a seriesId (null for one-offs),
// stable within a single response only: clients may group by it but must never persist it.
export const SyllabusEventSchema = z.object({
  // ISO date in the request's time zone. z.iso.date rejects impossible dates a pattern match
  // would pass, e.g. the 2026-02-31 a model can hallucinate off a syllabus table.
  date: z.iso.date(),
  // 24-hour "HH:MM"; null means all-day. Minute precision — seconds are not part of the
  // contract. Nullable fields default null because models routinely omit keys whose value would
  // be null, and an omission must not drop the event.
  time: z.iso.time({precision: -1}).nullable().default(null),
  // Only present when the syllabus states or clearly implies a duration; always null for
  // all-day events. Clients pick their own default when creating calendar entries from null.
  durationSeconds: z.number().int().positive().nullable().default(null),
  title: z.string().min(1),
  type: SyllabusEventTypeSchema,
  seriesId: z.string().min(1).nullable().default(null)
})
export type SyllabusEvent = z.infer<typeof SyllabusEventSchema>

export const SyllabusCourseSchema = z.object({
  name: z.string().min(1),
  // Human-readable one-line summary of the regular meeting schedule, e.g. "Tue · Thu · 2:00–3:15 PM"
  meetingTimeLine: z.string().min(1).nullable().default(null)
})
export type SyllabusCourse = z.infer<typeof SyllabusCourseSchema>

// All dated items are included, past ones too — clients filter to upcoming and derive analytics
// counts from the full list.
export const SyllabusParseResponseSchema = z.object({
  course: SyllabusCourseSchema,
  events: z.array(SyllabusEventSchema)
})
export type SyllabusParseResponse = z.infer<typeof SyllabusParseResponseSchema>

// {errorReason} error bodies; 422 terminal, 424 retryable (Learn convention). Not every error
// carries one — the auth middleware answers 401 as plain text, and nginx's backstop answers an
// oversized upload with a non-JSON 413 — so a missing one isn't proof the response wasn't ours.
export enum SyllabusParseErrorReason {
  // 404 — parsing is switched off server-side; treat the feature as unavailable, not failed
  FEATURE_DISABLED = 'FEATURE_DISABLED',
  // 400 — missing/invalid referenceDate, timezone, or locale query params
  INVALID_REQUEST = 'INVALID_REQUEST',
  // 400 — Content-Type isn't application/pdf; checked before the body is read
  UNSUPPORTED_CONTENT_TYPE = 'UNSUPPORTED_CONTENT_TYPE',
  // 400 — the body doesn't start with the PDF magic bytes (includes an empty body)
  INVALID_PDF = 'INVALID_PDF',
  // 413 — PDF over the size cap; re-exporting at lower quality is the user's recourse
  PDF_TOO_LARGE = 'PDF_TOO_LARGE',
  // 429 — per-user hourly cap exceeded; try again later
  RATE_LIMITED = 'RATE_LIMITED',
  // 503 — too many parses in flight on this pod; retry shortly. Distinct from RATE_LIMITED so
  // clients don't tell the user they've hit a personal limit.
  SERVER_BUSY = 'SERVER_BUSY',
  // 422 — the document isn't a course syllabus; retrying the same file won't help
  NOT_A_SYLLABUS = 'NOT_A_SYLLABUS',
  // 424 — the model responded with something other than JSON
  LLM_INVALID_JSON = 'LLM_INVALID_JSON',
  // 424 — the model returned JSON that doesn't match the contract
  LLM_IMPROPER_SCHEMA = 'LLM_IMPROPER_SCHEMA',
  // 424 — the model didn't finish within the server's cap
  LLM_TIMEOUT = 'LLM_TIMEOUT',
  // 500 — the route caught an unexpected exception reading the upload or calling the model. Named on
  // the wire so a client can tell this apart from a response that never reached the route at all.
  SERVER_ERROR = 'SERVER_ERROR',
  // 404 — no uploaded PDF at the job's object. The client's own upload never landed, so re-uploading
  // is the recourse; retrying the parse against the same job cannot succeed.
  UPLOAD_NOT_FOUND = 'UPLOAD_NOT_FOUND'
}

// Which model and resolution a parse runs on. Resolved from the requesting user's flags and stored,
// so a flag flipping mid-parse can't move a job between arms.
export enum SyllabusLlmArm {
  GEMINI = 'GEMINI',
  GEMINI_HIGH_RES = 'GEMINI_HIGH_RES',
  LUNA = 'LUNA'
}
